import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Globe, MapPin, Send, Loader2 } from "lucide-react";
import { LOGO, Reveal } from "../../lib/site";
import { scrollTo } from "./Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", phone: "", interest: "Rice", message: "" };
const INTERESTS = ["Rice", "Food Grade Products", "Pharmaceuticals", "Spices", "General Inquiry"];

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will get back to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-slate-500 focus:border-gold focus:outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="bg-navy py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Left: info + form */}
          <div>
            <Reveal>
              <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-gold">
                06 — Contact
              </span>
              <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-white text-balance">
                Let&apos;s build a reliable trade partnership
              </h2>
            </Reveal>

            <Reveal delay={1}>
              <div className="mt-10 space-y-5">
                <a href="mailto:info@amventures.co.in" data-testid="contact-email-link" className="group flex items-center gap-4 text-slate-200 hover:text-gold transition-colors">
                  <span className="grid h-11 w-11 place-items-center rounded-full glass text-gold"><Mail size={18} /></span>
                  info@amventures.co.in
                </a>
                <div className="flex items-center gap-4 text-slate-200">
                  <span className="grid h-11 w-11 place-items-center rounded-full glass text-gold"><Phone size={18} /></span>
                  <div className="flex flex-col">
                    <a href="tel:+919050091444" data-testid="contact-phone-1" className="hover:text-gold transition-colors">+91 90500-91444</a>
                    <a href="tel:+919456614000" data-testid="contact-phone-2" className="hover:text-gold transition-colors">+91 94566-14000</a>
                  </div>
                </div>
                <a href="https://www.amventures.co.in" target="_blank" rel="noreferrer" data-testid="contact-website-link" className="group flex items-center gap-4 text-slate-200 hover:text-gold transition-colors">
                  <span className="grid h-11 w-11 place-items-center rounded-full glass text-gold"><Globe size={18} /></span>
                  www.amventures.co.in
                </a>
                <a href="https://www.google.com/maps?q=Plot+No+152+Sector+3+HSIIDC+Karnal+Haryana+132001" target="_blank" rel="noreferrer" data-testid="contact-address-link" className="group flex items-start gap-4 text-slate-200 hover:text-gold transition-colors">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full glass text-gold"><MapPin size={18} /></span>
                  <span>Plot No. 152, Sector 3, HSIIDC,<br />Karnal, Haryana 132001, India</span>
                </a>
              </div>
            </Reveal>

            <form onSubmit={submit} data-testid="contact-form" className="mt-12 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="contact-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={upd("name")} />
                <input data-testid="contact-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={upd("email")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="contact-company-input" className={inputCls} placeholder="Company" value={form.company} onChange={upd("company")} />
                <input data-testid="contact-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={upd("phone")} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-slate-400">Interested in</label>
                <select data-testid="contact-interest-select" className={`${inputCls} appearance-none cursor-pointer`} value={form.interest} onChange={upd("interest")}>
                  {INTERESTS.map((i) => (
                    <option key={i} value={i} className="bg-navy-tint text-white">{i}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="contact-message-input" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your requirement *" value={form.message} onChange={upd("message")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-[#0B1F3A] transition-all duration-300 hover:bg-[#DDBE94] hover:scale-105 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="transition-transform group-hover:translate-x-1" />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Right: map */}
          <Reveal delay={1}>
            <div className="h-full min-h-[420px] overflow-hidden rounded-3xl border border-white/10 relative">
              <div className="absolute top-6 left-6 z-10 glass rounded-2xl px-5 py-4 flex items-center gap-3">
                <MapPin className="text-gold" size={20} />
                <div>
                  <p className="text-sm font-semibold text-white">AM Ventures</p>
                  <p className="text-xs text-slate-300">Karnal, Haryana 132001</p>
                </div>
              </div>
              <iframe
                title="AM Ventures location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Plot+No+152+Sector+3+HSIIDC+Karnal+Haryana+132001&output=embed"
                className="h-full w-full min-h-[420px] grayscale-[40%] contrast-110"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>

      <Footer />
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="mt-24 border-t border-white/10 pt-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
      <button onClick={() => scrollTo("hero")} className="flex items-center gap-3" data-testid="footer-logo-btn">
        <img src={LOGO} alt="AM Ventures" className="h-10 w-10 object-contain rounded-md" />
        <span className="font-display font-semibold tracking-wide text-white">AM <span className="text-gold">VENTURES</span></span>
      </button>
      <p className="text-sm text-slate-400">Connecting Markets. Delivering Trust.</p>
      <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} AM Ventures. All rights reserved.</p>
    </div>
  </footer>
);
